import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

interface Props {
  selectedMonth: string; // format: YYYY-MM
}

interface SummaryData {
  putawayPendingToday: number;
  putawayToday: number;
  putawayMonth: number;
  pickingPendingToday: number;
  pickingToday: number;
  pickingMonth: number;
}

interface DailyData {
  date: string; // format YYYY-MM-DD
  putaway: number;
  picking: number;
}

const PutawayPickingPerformance: React.FC<Props> = ({ selectedMonth }) => {
  const [summary, setSummary] = useState<SummaryData>({
    putawayPendingToday: 0,
    putawayToday: 0,
    putawayMonth: 0,
    pickingPendingToday: 0,
    pickingToday: 0,
    pickingMonth: 0,
  });

  const [dailyData, setDailyData] = useState<DailyData[]>([]);

  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    };
  };

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10); // format: YYYY-MM-DD

    axios
      .get(`${API}/analytics/putaway-picking-summary?date=${today}`, getAuthHeader())
      .then((res) => setSummary(res.data))
      .catch((err) => console.error('❌ Failed to fetch Putaway/Picking summary', err));

    axios
      .get(`${API}/analytics/putaway-picking-daily?month=${selectedMonth}`, getAuthHeader())
      .then((res) => setDailyData(res.data))
      .catch((err) =>
        console.error('❌ Failed to fetch Putaway/Picking daily performance', err)
      );
  }, [selectedMonth]);

  const formatDay = (dateStr: string) => {
    const [, , day] = dateStr.split('-');
    return day;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4">
          <h4 className="font-semibold text-base mb-2">Putaway</h4>
          <div className="grid grid-cols-3 gap-2">
            <SummaryCard label="Pending" value={summary.putawayPendingToday} bg="bg-blue-50" />
            <SummaryCard label="Completed Today" value={summary.putawayToday} bg="bg-blue-100" />
            <SummaryCard label="Completed This Month" value={summary.putawayMonth} bg="bg-blue-200" />
          </div>
        </div>
        <div className="bg-white rounded shadow p-4">
          <h4 className="font-semibold text-base mb-2">Picking</h4>
          <div className="grid grid-cols-3 gap-2">
            <SummaryCard label="Pending" value={summary.pickingPendingToday} bg="bg-green-50" />
            <SummaryCard label="Completed Today" value={summary.pickingToday} bg="bg-green-100" />
            <SummaryCard label="Completed This Month" value={summary.pickingMonth} bg="bg-green-200" />
          </div>
        </div>
      </div>

      {/* Putaway Chart */}
      <div className="bg-white p-4 rounded shadow">
        <h3 className="text-base font-semibold mb-2">Completed Putaway (This Month)</h3>
        <div style={{ width: '100%', height: 300, fontSize: 13 }}>
          <ResponsiveContainer>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDay} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="putaway" fill="#3b82f6" name="Putaway" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Picking Chart */}
      <div className="bg-white p-4 rounded shadow">
        <h3 className="text-base font-semibold mb-2">Completed Picking (This Month)</h3>
        <div style={{ width: '100%', height: 300, fontSize: 13 }}>
          <ResponsiveContainer>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDay} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="picking" fill="#10b981" name="Picking" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({
  label,
  value,
  bg,
}: {
  label: string;
  value: number;
  bg: string;
}) => (
  <div className={`p-2 rounded ${bg}`}>
    <p className="text-sm text-gray-600 capitalize">{label}</p>
    <p className="text-lg font-bold text-gray-900">{value}</p>
  </div>
);

export default PutawayPickingPerformance;
